/**
 * USE-COUNT-UP
 * Anime une valeur numérique de 0 vers `target` (ease-out cubic) au lieu d'un
 * affichage figé — réutilisé partout où un chiffre de dashboard doit "compter"
 * (participation, score de la rosace...).
 */
import { useEffect, useRef, useState } from "react"
import { useAnimationsDisabled } from "./animation-mode.utils"

interface CountUpOptions {
  duration?: number //ms, défaut 900
}

export function useCountUp(target: number, options: CountUpOptions = {}): number {
  const { duration = 900 } = options
  const [value, setValue] = useState(0)
  const valueRef = useRef(0)
  //Export ou « réduire les animations » : la valeur finale directement
  const isDisabled = useAnimationsDisabled()

  useEffect(() => {
    if (!isFinite(target) || isDisabled) {
      setValue(target)
      valueRef.current = target
      return
    }

    const startValue = valueRef.current
    const startTime = performance.now()
    let frame: number

    function tick(now: number) {
      const progress = Math.min(1, (now - startTime) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = startValue + (target - startValue) * eased

      setValue(next)
      valueRef.current = next

      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [target, duration, isDisabled])

  return isDisabled ? target : value
}

//Variante liste : anime plusieurs valeurs en parallèle (ex: les 10 barres d'une répartition)
//avec une seule boucle rAF, plutôt que d'appeler useCountUp dans une .map (interdit si la
//longueur du tableau peut varier d'un render à l'autre — casse les rules of hooks)
export function useCountUpList(targets: number[], options: CountUpOptions = {}): number[] {
  const { duration = 900 } = options
  const [values, setValues] = useState<number[]>(() => targets.map(() => 0))
  const valuesRef = useRef<number[]>(targets.map(() => 0))
  const targetsKey = targets.join(",")
  const isDisabled = useAnimationsDisabled()

  useEffect(() => {
    if (isDisabled) {
      valuesRef.current = targets
      return
    }

    const startValues = targets.map((_, i) => valuesRef.current[i] ?? 0)
    const startTime = performance.now()
    let frame: number

    function tick(now: number) {
      const progress = Math.min(1, (now - startTime) / duration)
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = targets.map((target, i) => startValues[i] + (target - startValues[i]) * eased)

      setValues(next)
      valuesRef.current = next

      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey, duration, isDisabled])

  return isDisabled ? targets : values
}
